import Papa from 'papaparse';
import type { Paper } from '../types';

// Generate unique ID
const generateId = () => Math.random().toString(36).substring(2, 11);

// Parse CSV file
export async function parseCSV(file: File): Promise<Paper[]> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),
      complete: (results) => {
        try {
          const papers = results.data.map((row: any, index: number) => {
            // Try different column name variations
            const pmid = row['PMID'] || row['pmid'] || row['PubMed ID'] || row['pubmed_id'] || String(index + 1);
            const title = row['Title'] || row['title'] || row['Article Title'] || row['article_title'] || '';
            const authors = row['Authors'] || row['authors'] || row['Author'] || '';
            const year = row['Year'] || row['year'] || row['Publication Year'] || '';
            const journal = row['Journal'] || row['journal'] || row['Source'] || '';
            const abstract = row['Abstract'] || row['abstract'] || '';
            const doi = row['DOI'] || row['doi'] || '';

            return {
              id: generateId(),
              pmid: String(pmid).trim(),
              title: String(title).trim(),
              authors: authors ? String(authors).trim() : undefined,
              year: year ? String(year).trim() : undefined,
              journal: journal ? String(journal).trim() : undefined,
              abstract: abstract ? String(abstract).trim() : undefined,
              doi: doi ? String(doi).trim() : undefined,
              matchedInclusionKeywords: [],
              matchedExclusionKeywords: [],
            } as Paper;
          }).filter((paper) => paper.title || paper.pmid);

          resolve(papers);
        } catch (error) {
          reject(new Error('Failed to parse CSV data'));
        }
      },
      error: (error) => {
        reject(new Error(`CSV parsing error: ${error.message}`));
      },
    });
  });
}

// Parse Excel file (XLS/XLSX)
export async function parseExcel(file: File): Promise<Paper[]> {
  const XLSX = await import('xlsx');

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });

        // Get first sheet
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        const papers = jsonData.map((row: any, index: number) => {
          const pmid = row['PMID'] || row['pmid'] || row['PubMed ID'] || String(index + 1);
          const title = row['Title'] || row['title'] || row['Article Title'] || '';
          const authors = row['Authors'] || row['authors'] || row['Author'] || '';
          const year = row['Year'] || row['year'] || row['Publication Year'] || '';
          const journal = row['Journal'] || row['journal'] || row['Source'] || '';
          const abstract = row['Abstract'] || row['abstract'] || '';
          const doi = row['DOI'] || row['doi'] || '';

          return {
            id: generateId(),
            pmid: String(pmid).trim(),
            title: String(title).trim(),
            authors: authors ? String(authors).trim() : undefined,
            year: year ? String(year).trim() : undefined,
            journal: journal ? String(journal).trim() : undefined,
            abstract: abstract ? String(abstract).trim() : undefined,
            doi: doi ? String(doi).trim() : undefined,
            matchedInclusionKeywords: [],
            matchedExclusionKeywords: [],
          } as Paper;
        }).filter((paper) => paper.title || paper.pmid);

        resolve(papers);
      } catch (error) {
        reject(new Error('Failed to parse Excel file'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsArrayBuffer(file);
  });
}

// Auto-detect file type and parse
export async function parseFile(file: File): Promise<Paper[]> {
  const extension = file.name.split('.').pop()?.toLowerCase();

  if (extension === 'csv') {
    return parseCSV(file);
  } else if (extension === 'xls' || extension === 'xlsx') {
    return parseExcel(file);
  } else {
    throw new Error(`Unsupported file format: ${extension}. Please use CSV or Excel files.`);
  }
}

// Parse keywords from text
export function parseKeywordsText(text: string): { inclusion: string[]; exclusion: string[] } {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  let currentSection: 'inclusion' | 'exclusion' | null = null;
  const inclusion: string[] = [];
  const exclusion: string[] = [];

  for (const line of lines) {
    const lower = line.toLowerCase();
    if (lower === 'inclusion') {
      currentSection = 'inclusion';
    } else if (lower === 'exclusion') {
      currentSection = 'exclusion';
    } else if (currentSection === 'inclusion') {
      inclusion.push(line);
    } else if (currentSection === 'exclusion') {
      exclusion.push(line);
    }
  }

  return { inclusion, exclusion };
}
